import React from 'react';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogFooter,
  Button,
  TextField,
  TextArea,
  Select,
  useToast,
} from '@chatsdet/ui';
import { Plus } from '@phosphor-icons/react';
import { getBoardManifest, type BoardType } from '@chatsdet/cards-plugins';
import { useCreateCard } from '../hooks/useCards';

interface Props {
  boardType: BoardType;
  /** Optional default stage to pre-select (defaults to first stage). */
  defaultStage?: string;
  /** Optional render-prop for board-specific custom fields (e.g. account company name). */
  renderDetailFields?: (
    details: Record<string, unknown>,
    setDetails: (next: Record<string, unknown>) => void
  ) => React.ReactNode;
}

export function CreateCardDialog({ boardType, defaultStage, renderDetailFields }: Props) {
  const manifest = getBoardManifest(boardType);
  const initialStage = defaultStage ?? manifest.stages[0]?.value ?? '';

  const [isOpen, setIsOpen] = React.useState(false);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [stage, setStage] = React.useState(initialStage);
  const [details, setDetails] = React.useState<Record<string, unknown>>({});

  const create = useCreateCard(boardType);
  const { toast } = useToast();

  const reset = () => {
    setTitle('');
    setDescription('');
    setStage(initialStage);
    setDetails({});
  };

  const canSubmit = title.trim().length > 0 && stage.length > 0 && !create.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;
    create.mutate(
      {
        title: title.trim(),
        description: description.trim() || undefined,
        stage,
        details: Object.keys(details).length > 0 ? details : undefined,
      },
      {
        onSuccess: () => {
          toast.success(`${manifest.label} created`);
          reset();
          setIsOpen(false);
        },
        onError: () => toast.error('Failed to create'),
      }
    );
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={setIsOpen} size="md">
      <Button variant="primary" size="sm" iconLeft={<Plus weight="bold" size={16} />}>
        New {manifest.label.replace(/s$/, '') || 'Card'}
      </Button>
      <Dialog title={`Create ${manifest.label.replace(/s$/, '') || 'Card'}`}>
        <DialogContent>
          <TextField label="Title" value={title} onChange={setTitle} />
          <TextArea
            label="Description"
            value={description}
            onChange={setDescription}
            className="min-h-[100px]"
          />
          <Select
            label="Stage"
            items={manifest.stages.map((s) => ({ id: s.value, label: s.label }))}
            selectedKey={stage}
            onSelectionChange={(key) => setStage(String(key))}
          />
          {renderDetailFields?.(details, setDetails)}
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button variant="primary" onPress={handleSubmit} isDisabled={!canSubmit}>
            {create.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
